import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar, MapPin, Clock, Volume2, VolumeX, Sparkles, Navigation, Send, Heart, Share2, CheckCircle2, ChevronDown, Film } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import './theme.css';

const META: TemplateMetadata = {
  id: 'video-01-royal-gold',
  name: 'Royal Gold Video — Animated Ceremonies',
  occasionType: 'WEDDING',
  description: 'A breathtaking, cinematic Indian wedding invitation powered by real-time video backgrounds, floral board framing, ceremony caricature art, and dynamic typography.',
  isPremium: true,
  tags: ['video', 'royal', 'gold', 'cinematic', 'animated', 'premium', 'scroll-experience', 'marathi', 'wedding', 'haldi', 'mehendi', 'sangeet', 'reception', 'vocal', 'wow'],
  colorScheme: { primary: '#D4AF37', accent: '#7B1E26', bg: '#0D0500' },
};

interface CeremonySpec {
  id: string;
  name: string;
  marathiName: string;
  videoBg: string;
  coupleImg?: string;
  defaultDate: string;
  defaultTime: string;
  description: string;
}

const CEREMONIES: CeremonySpec[] = [
  {
    id: 'poojan',
    name: 'Ganesh Poojan',
    marathiName: 'श्री गणेश पूजन',
    videoBg: '/assets/templates/video-01-royal/poojan_bg.mp4',
    defaultDate: '11th November 2026',
    defaultTime: '08:00 AM Onwards',
    description: 'Seeking divine blessings of Lord Ganesha for an auspicious start.',
  },
  {
    id: 'haldi',
    name: 'Haldi Ceremony',
    marathiName: 'हळदी समारंभ',
    videoBg: '/assets/templates/video-01-royal/haldi_bg.mp4',
    coupleImg: '/assets/templates/video-01-royal/haldi_couple.png',
    defaultDate: '12th November 2026',
    defaultTime: '09:00 AM Onwards',
    description: 'Bathing the couple in auspicious turmeric and golden floral blessings.',
  },
  {
    id: 'mehendi',
    name: 'Mehendi Ceremony',
    marathiName: 'मेहंदी सोहळा',
    videoBg: '/assets/templates/video-01-royal/mehendi_bg.mp4',
    coupleImg: '/assets/templates/video-01-royal/mehendi_couple.png',
    defaultDate: '12th November 2026',
    defaultTime: '04:00 PM Onwards',
    description: 'Adorning hands with intricate henna designs and celebration songs.',
  },
  {
    id: 'sangeet',
    name: 'Sangeet Night',
    marathiName: 'संगीत सोहळा',
    videoBg: '/assets/templates/video-01-royal/sangeet_bg.mp4',
    defaultDate: '13th November 2026',
    defaultTime: '07:00 PM Onwards',
    description: 'An evening of dance, music, and joyful family performances.',
  },
  {
    id: 'wedding',
    name: 'Shubh Vivah (Wedding)',
    marathiName: 'शुभ विवाह',
    videoBg: '/assets/templates/video-01-royal/wedding_bg.mp4',
    defaultDate: '14th November 2026',
    defaultTime: '12:35 PM (Shubh Muhurat)',
    description: 'Sacred seven vows around the holy fire to unite two souls forever.',
  },
  {
    id: 'reception',
    name: 'Grand Reception',
    marathiName: 'स्नेहभोजन व लग्नकार्याचे स्वागत',
    videoBg: '/assets/templates/video-01-royal/reception_bg.mp4',
    coupleImg: '/assets/templates/video-01-royal/reception_couple.png',
    defaultDate: '14th November 2026',
    defaultTime: '07:00 PM Onwards',
    description: 'A grand evening banquet celebrating the newlyweds with family and friends.',
  },
];

export function VideoRoyal01Template({ data }: { data: InvitationData }) {
  const weddingData = (data.customData as any)?.wedding || {};
  const expMeta = (data.customData as any)?.experience?.meta || {};
  const groomName = weddingData.groom?.nickname || weddingData.groom?.fullName || expMeta.groomName || expMeta.secondaryName || (data.eventName ? data.eventName.split(/ (?:&|and) /i)[0] : '') || 'Aarav';
  const brideName = weddingData.bride?.nickname || weddingData.bride?.fullName || expMeta.brideName || expMeta.primaryName || (data.eventName ? data.eventName.split(/ (?:&|and) /i)[1] : '') || 'Ananya';
  const weddingDate = (data as any)._dateInput || data.eventDate || expMeta.weddingDate || expMeta.eventDate || '14th November 2026';
  const venueName = data.eventVenue || 'The Grand Palace Resort';
  const hostNames = data.hostName || (data.customData as any)?.hostNames || 'The Sharma & Mehta Families';
  const bridePhoto = expMeta.referenceImages?.bride || expMeta.bridePhotoUrl || (data.customData as any)?.bridePhotoUrl;
  const groomPhoto = expMeta.referenceImages?.groom || expMeta.groomPhotoUrl || (data.customData as any)?.groomPhotoUrl;
  const couplePhoto = expMeta.referenceImages?.couple || expMeta.couplePhotoUrl || expMeta.referenceImages?.primary || (data.customData as any)?.couplePhotoUrl;
  const customVideoUrl = (data.customData as any)?.customVideoUrl || expMeta.customVideoUrl || (data.customData as any)?.preWeddingVideoUrl;

  const [activeVideoBg, setActiveVideoBg] = useState<string>(CEREMONIES[0].videoBg);
  const [activeSectionId, setActiveSectionId] = useState<string>('section-hero');
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [showRsvpModal, setShowRsvpModal] = useState<boolean>(false);
  const [showShareToast, setShowShareToast] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Scroll observer to update video background dynamically as user scrolls
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setActiveSectionId(sectionId);
            const foundCeremony = CEREMONIES.find((c) => `section-${c.id}` === sectionId);
            if (foundCeremony) {
              setActiveVideoBg(foundCeremony.videoBg);
            } else if (sectionId === 'section-hero') {
              setActiveVideoBg(CEREMONIES[0].videoBg);
            } else if (sectionId === 'section-rsvp') {
              setActiveVideoBg(CEREMONIES[CEREMONIES.length - 1].videoBg);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '-10% 0px -10% 0px' }
    );

    const sections = document.querySelectorAll('.scroll-ceremony-section');
    sections.forEach((sec) => observer.observe(sec));

    return () => {
      sections.forEach((sec) => observer.unobserve(sec));
    };
  }, []);

  useEffect(() => {
    audioRef.current = new Audio('/assets/templates/video-01-royal/audio.mp3');
    audioRef.current.loop = true;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlayingAudio) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
    } else {
      audioRef.current.play().then(() => setIsPlayingAudio(true)).catch(() => {});
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Royal Wedding Invitation: ${groomName} & ${brideName}`,
        text: `You are cordially invited to the Royal Wedding of ${groomName} & ${brideName}.`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    }
  };

  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculate = () => {
      const target = +new Date(weddingDate) || +new Date('2026-11-14T12:00:00');
      const diff = target - +new Date();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    };
    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [weddingDate]);

  return (
    <div className="relative min-h-screen bg-[#0D0500] text-amber-50 font-sans selection:bg-amber-500 selection:text-black overflow-x-hidden">
      
      {/* Background Video Layer with Zoom-Out Full Frame Crossfade */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-black">
        {CEREMONIES.map((ceremony) => {
          const isActive = activeVideoBg === ceremony.videoBg;
          return (
            <video
              key={ceremony.id}
              autoPlay
              loop
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover object-center scale-100 filter brightness-110 contrast-105 saturate-110 transition-opacity duration-700 ${
                isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              src={ceremony.videoBg}
            />
          );
        })}

        {/* Crisp Ambient Vignette & Vibrant Video Pass-Through */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/65 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/25 to-black/70 pointer-events-none" />
      </div>

      {/* Floating Audio Control */}
      <button
        onClick={toggleAudio}
        aria-label="Toggle Ceremony Audio"
        className="fixed top-4 right-4 z-50 p-2.5 rounded-full bg-amber-950/85 border border-amber-500/50 text-amber-300 backdrop-blur-md shadow-lg hover:scale-110 active:scale-95 transition-all cursor-pointer"
      >
        {isPlayingAudio ? <Volume2 className="w-4 h-4 animate-pulse text-amber-300" /> : <VolumeX className="w-4 h-4 text-amber-400/60" />}
      </button>

      {/* Sticky Quick Jump Navigation Bar */}
      <div className="sticky top-2 z-40 max-w-xl mx-auto px-3 my-2">
        <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-black/80 border border-amber-500/40 backdrop-blur-md overflow-x-auto scrollbar-none shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
          <button
            onClick={() => scrollToSection('section-hero')}
            className={`px-3 py-1 rounded-xl text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap transition-all cursor-pointer ${
              activeSectionId === 'section-hero'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
            }`}
          >
            Home
          </button>
          {CEREMONIES.map((ceremony) => (
            <button
              key={ceremony.id}
              onClick={() => scrollToSection(`section-${ceremony.id}`)}
              className={`px-3 py-1 rounded-xl text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeSectionId === `section-${ceremony.id}`
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                  : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
              }`}
            >
              {ceremony.name.split(' ')[0]}
            </button>
          ))}
          <button
            onClick={() => scrollToSection('section-rsvp')}
            className={`px-3 py-1 rounded-xl text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap transition-all cursor-pointer ${
              activeSectionId === 'section-rsvp'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
            }`}
          >
            RSVP
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showShareToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-amber-500 text-amber-950 font-bold text-xs shadow-lg flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" /> Link copied to clipboard!
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Scroll Container */}
      <main className="relative z-10 max-w-xl mx-auto px-3 py-4 space-y-12 sm:space-y-16">
        
        {/* SECTION 0: HERO & WELCOME HEADER */}
        <section id="section-hero" className="scroll-ceremony-section min-h-[85vh] flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="glass-gold-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)] flex flex-col items-center"
          >
            {/* Floral Board Frame Overlay */}
            <img
              src="/assets/templates/video-01-royal/board_frame.png"
              alt="Floral Frame"
              className="absolute inset-0 w-full h-full object-fill opacity-25 pointer-events-none"
            />

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold uppercase tracking-widest backdrop-blur-md mb-2">
              <Sparkles className="w-3 h-3 text-amber-400" /> Royal Wedding Invitation <Sparkles className="w-3 h-3 text-amber-400" />
            </div>

            <p className="text-xs sm:text-sm text-amber-200/90 tracking-widest uppercase mb-1">
              || {hostNames} ||
            </p>

            <h1 className="font-elsie text-4xl sm:text-6xl font-bold text-gold-shimmer drop-shadow-[0_2px_15px_rgba(212,175,55,0.4)] tracking-wide my-2">
              {groomName} <span className="text-2xl sm:text-3xl text-amber-400 font-elsie">&amp;</span> {brideName}
            </h1>

            <p className="font-im-fell italic text-amber-200/90 text-xs sm:text-sm max-w-md my-2">
              "Together with their families, cordially invite you to celebrate their auspicious wedding union."
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-3 border-t border-amber-500/20 text-xs text-amber-300 w-full">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{weddingDate}</span>
              </div>
              <span className="hidden sm:inline text-amber-500/60">•</span>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="truncate">{venueName}</span>
              </div>
            </div>

            {/* Uploaded Bride & Groom Showcase Section */}
            {(bridePhoto || groomPhoto || couplePhoto) && (
              <div className="my-6 grid grid-cols-2 gap-4 w-full">
                {groomPhoto && (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_25px_rgba(212,175,55,0.6)] overflow-hidden">
                      <img src={groomPhoto} alt={groomName} className="w-full h-full object-cover rounded-full" />
                    </div>
                    <span className="mt-2 text-xs font-bold text-amber-200 tracking-wider">{groomName}</span>
                    <span className="text-[10px] text-amber-300/70 uppercase">Groom</span>
                  </div>
                )}
                {bridePhoto && (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_25px_rgba(212,175,55,0.6)] overflow-hidden">
                      <img src={bridePhoto} alt={brideName} className="w-full h-full object-cover rounded-full" />
                    </div>
                    <span className="mt-2 text-xs font-bold text-amber-200 tracking-wider">{brideName}</span>
                    <span className="text-[10px] text-amber-300/70 uppercase">Bride</span>
                  </div>
                )}
                {couplePhoto && !bridePhoto && !groomPhoto && (
                  <div className="col-span-2 flex flex-col items-center my-2">
                    <div className="w-full max-w-sm h-48 sm:h-56 rounded-2xl p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_30px_rgba(212,175,55,0.5)] overflow-hidden">
                      <img src={couplePhoto} alt={`${groomName} & ${brideName}`} className="w-full h-full object-cover rounded-xl" />
                    </div>
                    <span className="mt-2 text-xs font-bold text-amber-200 tracking-wider">{groomName} &amp; {brideName}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => setShowRsvpModal(true)}
                className="py-2.5 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-amber-950 font-bold text-xs shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:scale-105 active:scale-95 transition-all cursor-pointer"
              >
                Confirm RSVP
              </button>
            </div>

            {/* Scroll Indicator */}
            <div className="mt-8 flex flex-col items-center text-amber-400/80 text-[11px] uppercase tracking-wider animate-bounce">
              <span>Scroll down for Ceremonies</span>
              <ChevronDown className="w-4 h-4 mt-0.5" />
            </div>
          </motion.div>
        </section>

        {/* SECTIONS 1 TO 6: INDIVIDUAL CEREMONY CARDS */}
        {CEREMONIES.map((ceremony) => (
          <section
            key={ceremony.id}
            id={`section-${ceremony.id}`}
            className="scroll-ceremony-section py-4 min-h-[70vh] flex flex-col items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="glass-gold-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_45px_rgba(0,0,0,0.85)] flex flex-col items-center"
            >
              {/* Floral Board Frame Overlay */}
              <img
                src="/assets/templates/video-01-royal/board_frame.png"
                alt="Floral Frame"
                className="absolute inset-0 w-full h-full object-fill opacity-20 pointer-events-none"
              />

              <span className="font-elsie text-amber-400/90 text-xs sm:text-sm tracking-widest uppercase block mb-1">
                {ceremony.marathiName}
              </span>

              <h2 className="font-vidaloka text-2xl sm:text-4xl text-amber-200 drop-shadow-[0_2px_10px_rgba(212,175,55,0.4)] py-1">
                {ceremony.name}
              </h2>

              {/* Ceremony Illustration */}
              {ceremony.coupleImg && (
                <div className="relative z-10 my-3 w-32 sm:w-44 max-h-40 flex justify-center">
                  <img
                    src={ceremony.coupleImg}
                    alt={ceremony.name}
                    className="w-full h-full object-contain filter drop-shadow-[0_6px_15px_rgba(0,0,0,0.7)]"
                  />
                </div>
              )}

              <p className="font-im-fell text-amber-100/90 text-xs sm:text-sm max-w-md my-3 leading-relaxed">
                {ceremony.description}
              </p>

              {/* Timing & Date Details */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-2xl bg-black/50 border border-amber-500/30 text-xs text-left mb-4">
                <div className="flex items-center gap-2 text-amber-200">
                  <Calendar className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>{weddingDate || ceremony.defaultDate}</span>
                </div>
                <div className="flex items-center gap-2 text-amber-200">
                  <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>{ceremony.defaultTime}</span>
                </div>
              </div>

              {/* Venue Info */}
              <div className="w-full p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between gap-3 text-xs text-left">
                <div className="space-y-0.5 min-w-0">
                  <div className="flex items-center gap-1.5 text-amber-300 font-semibold truncate">
                    <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="truncate">{venueName}</span>
                  </div>
                </div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(venueName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 flex items-center gap-1 text-[11px] font-semibold transition-colors shrink-0"
                >
                  <Navigation className="w-3 h-3" /> Directions
                </a>
              </div>
            </motion.div>
          </section>
        ))}

        {/* SECTION: CUSTOM UPLOADED PRE-WEDDING VIDEO */}
        {customVideoUrl && (
          <section id="section-prewedding-video" className="scroll-ceremony-section py-4 min-h-[50vh] flex flex-col items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="glass-gold-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)] space-y-4"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold tracking-widest uppercase">
                <Film className="w-3.5 h-3.5" /> Pre-Wedding Story
              </div>
              <h2 className="font-vidaloka text-3xl sm:text-4xl text-amber-300">Our Special Memories</h2>
              <div className="relative rounded-2xl overflow-hidden border border-amber-500/40 shadow-2xl bg-black/90">
                <video
                  src={customVideoUrl}
                  controls
                  playsInline
                  className="w-full max-h-[420px] object-contain rounded-2xl"
                />
              </div>
            </motion.div>
          </section>
        )}

        {/* SECTION 7: COUNTDOWN, VENUE MAP & RSVP FOOTER */}
        <section id="section-rsvp" className="scroll-ceremony-section py-4 min-h-[75vh] flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="glass-gold-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)] space-y-6 flex flex-col items-center"
          >
            <div>
              <Heart className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-pulse" />
              <h2 className="font-vidaloka text-2xl sm:text-3xl text-amber-200">Countdown &amp; RSVP</h2>
              <p className="font-im-fell text-xs sm:text-sm text-amber-200/80 mt-1">
                Counting down the moments until we celebrate together!
              </p>
            </div>

            {/* Countdown Timer */}
            <div className="grid grid-cols-4 gap-2 w-full max-w-md my-2">
              {[
                { label: 'Days', value: timeLeft.days },
                { label: 'Hours', value: timeLeft.hours },
                { label: 'Mins', value: timeLeft.minutes },
                { label: 'Secs', value: timeLeft.seconds },
              ].map((unit, i) => (
                <div key={i} className="p-3 rounded-2xl bg-black/60 border border-amber-500/30 text-center">
                  <span className="font-elsie text-xl sm:text-2xl font-bold text-amber-300 block">{unit.value}</span>
                  <span className="text-[10px] text-amber-200/70 uppercase tracking-widest">{unit.label}</span>
                </div>
              ))}
            </div>

            {/* Venue Address Info */}
            <div className="w-full p-4 rounded-2xl bg-black/60 border border-amber-500/40 text-left space-y-2">
              <div className="flex items-center gap-2 text-amber-300 font-bold text-sm">
                <MapPin className="w-4 h-4 text-amber-400" />
                <span>{venueName}</span>
              </div>
              <p className="text-xs text-amber-300/90 pl-6 font-semibold">Hosted By: {hostNames}</p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(venueName)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 text-xs font-semibold transition-colors"
              >
                <Navigation className="w-3.5 h-3.5" /> Open Google Maps Directions
              </a>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2 w-full">
              <button
                onClick={() => setShowRsvpModal(true)}
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-amber-950 font-bold text-xs shadow-[0_0_25px_rgba(212,175,55,0.5)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Send className="w-4 h-4" /> Confirm Your RSVP
              </button>

              <button
                onClick={handleShare}
                className="w-full py-3 px-6 rounded-xl bg-amber-950/90 border border-amber-400/50 text-amber-300 text-xs font-semibold hover:bg-amber-900/90 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Share2 className="w-4 h-4" /> Share Invitation
              </button>
            </div>
          </motion.div>
        </section>

      </main>

      {/* RSVP Modal */}
      <AnimatePresence>
        {showRsvpModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="glass-gold-card max-w-md w-full p-6 rounded-3xl relative text-center border border-amber-500/50"
            >
              <button
                onClick={() => setShowRsvpModal(false)}
                className="absolute top-4 right-4 text-amber-400 hover:text-amber-200 text-lg cursor-pointer"
              >
                ✕
              </button>
              <Heart className="w-7 h-7 text-amber-400 mx-auto mb-1" />
              <h3 className="font-vidaloka text-lg text-amber-200 mb-1">RSVP Confirmation</h3>
              <p className="text-xs text-amber-200/80 mb-4">
                Please let us know if you will be joining the royal celebration for {groomName} &amp; {brideName}.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setShowRsvpModal(false);
                  alert('Thank you! Your RSVP response has been recorded.');
                }}
                className="space-y-3 text-left"
              >
                <div>
                  <label className="text-xs text-amber-300 font-semibold">Your Full Name</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Rajesh Sharma"
                    className="w-full mt-1 px-3 py-2 rounded-xl bg-black/60 border border-amber-500/30 text-amber-100 text-sm focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="text-xs text-amber-300 font-semibold">Attending Guests Count</label>
                  <select className="w-full mt-1 px-3 py-2 rounded-xl bg-black/60 border border-amber-500/30 text-amber-100 text-sm focus:outline-none focus:border-amber-400">
                    <option value="1">1 Person</option>
                    <option value="2">2 Persons</option>
                    <option value="3">Family (3+ Persons)</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-amber-950 font-bold text-sm shadow-md hover:scale-[1.02] transition-transform mt-1 cursor-pointer"
                >
                  Submit RSVP
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Self-register in template registry
registry.register({
  metadata: META,
  component: VideoRoyal01Template,
});
